import { z } from 'zod';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const KYC_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

// The four document types the real API accepts — shared by the investor
// self-service KYC form and the admin Onboard Client form (same selector on
// both, per the docs).
export const KYC_DOCUMENT_TYPES = [
  { value: 'national_id', label: 'National ID (NIN)' },
  { value: 'passport', label: 'International Passport' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'utility_bill', label: 'Utility Bill' },
];

const KYC_DOCUMENT_TYPE_VALUES = KYC_DOCUMENT_TYPES.map((t) => t.value);

// Single-file rule, reused everywhere a document gets uploaded: jpg/jpeg/
// png/pdf, max 5MB. Client-side pre-check only — the server 422s on
// `documents`/`photo`-style fields as the real source of truth.
export const kycFileSchema = z
  .instanceof(File)
  .refine((file) => KYC_ALLOWED_MIME_TYPES.includes(file.type), 'Must be a JPG, PNG, or PDF file.')
  .refine((file) => file.size <= MAX_FILE_BYTES, 'Each file must be 5MB or smaller.');

export const kycDocumentTypeSchema = z.enum(KYC_DOCUMENT_TYPE_VALUES, {
  errorMap: () => ({ message: 'Select a document type' }),
});

// POST /investor/kyc — one document_type, 1-2 files.
export const kycSubmissionSchema = z.object({
  document_type: kycDocumentTypeSchema,
  documents: z.array(kycFileSchema).min(1, 'Upload at least one document').max(2, 'Upload at most 2 documents'),
});

// v1.18.0 — "other" (non-identity) document tags. The spec explicitly
// warns against guessing the full backend enum and says to confirm it
// against a live response before finalizing — that check couldn't happen
// this session (network unreachable), so per the user's own explicit
// fallback choice this ships with just the two known examples from the
// spec, PLUS free text everywhere a tag is picked by an admin (Add
// Template, Attach Document) so a real tag this list doesn't know about
// still works — the backend's 422 is the actual source of truth, this list
// is only a convenience shortcut for the common cases.
export const KYC_OTHER_DOCUMENT_TAGS = [
  { value: 'fixed_income_agreement', label: 'Fixed Income Agreement' },
  { value: 'kyc_form', label: 'Cavan Capital KYC Form' },
];

// Deliberately permissive (not a z.enum) — an admin must be able to type a
// tag this app doesn't know about in advance rather than being blocked by
// client-side validation; the server enforces the real rule.
export const kycOtherDocumentTagSchema = z
  .string()
  .trim()
  .min(1, 'Enter or select a tag')
  .max(64, 'Tag is too long');
